/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * Adds a product to the recently viewed products list.
 * @param {string | { id: string, handle?: string, title?: string, url?: string, image?: string, hoverImage?: string, price?: string, variants?: unknown[] }} product - The product to add.
   */
  static addProduct(product) {
    const normalizedProduct = this.#normalizeProduct(product);
    if (!normalizedProduct?.id) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((item) => this.#normalizeProduct(item)?.id !== normalizedProduct.id);
    viewedProducts.unshift(normalizedProduct);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {{ id: string, handle?: string, title?: string, url?: string, image?: string, hoverImage?: string, price?: string, variants?: unknown[] }[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const products = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return Array.isArray(products) ? products.map((item) => this.#normalizeProduct(item)).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  static #normalizeProduct(product) {
    if (!product) return null;

    if (typeof product === 'string' || typeof product === 'number') {
      return { id: String(product) };
    }

    if (typeof product !== 'object') return null;

    const id = product.id ? String(product.id) : '';
    if (!id) return null;

    return {
      id,
      handle: product.handle ? String(product.handle) : '',
      title: product.title ? String(product.title) : '',
      url: product.url ? String(product.url) : '',
      image: product.image ? String(product.image) : '',
      hoverImage: product.hoverImage ? String(product.hoverImage) : '',
      price: product.price ? String(product.price) : '',
      variants: Array.isArray(product.variants)
        ? product.variants
            .map((variant) => ({
              color: variant?.color ? String(variant.color) : '',
              url: variant?.url ? String(variant.url) : '',
              image: variant?.image ? String(variant.image) : '',
            }))
            .filter((variant) => variant.color)
        : [],
    };
  }
}
